/* eslint-disable import/order */
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";

import { deleteTeam, searchTeams, createCustomTeam } from "../../redux/slices/teamsSlice";
import {
  inviteMember,
  deleteMember,
  bulkDeleteMembers,
  bulkDeleteTeams,
} from "../../redux/slices/userSlice";
import { updateCategoryLocally, inviteTeam } from "../../redux/slices/tournamentSlice";
import { authApi } from "../../utils/axios";
import teamBadgePlaceholder from "../../assets/images/team_badge_placeholder.svg";
import { fixedValues } from "../../utils/fixedValues";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import { participantAdded } from "../../utils/analytics";
import { PARTICIPANT_TYPE } from "../../utils/analyticsEvents";

import { TAB_TYPES, MODAL_TYPES, STATUS_PRIORITY, ROWS_PER_PAGE_OPTIONS, validateEmail } from "./constants";
import Header from "./components/Header";
import TabNav from "./components/TabNav";
import SearchFilters from "./components/SearchFilters";
import ParticipantsTable from "./components/ParticipantsTable";
import EmptyState from "./components/EmptyState";
import ActionMenu from "./components/ActionMenu";
import AddModal from "./components/modals/AddModal";
import DeleteModal from "./components/modals/DeleteModal";
import useTournamentRole from "../../hooks/useTournamentRole";
/* eslint-enable import/order */

/**
 * Participants Page
 * Manages Teams, Referees, and Administrators for a competition
 */
const Participants = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );
  const { searchResults, isSearching } = useSelector((state) => state.teams);
  const { permissions } = useTournamentRole();

  // Tab State
  const [activeTab, setActiveTab] = useState(TAB_TYPES.TEAMS);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortBy, setSortBy] = useState("status");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  // Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addModalType, setAddModalType] = useState(MODAL_TYPES.TEAM);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Form State
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [selectedTeamsToInvite, setSelectedTeamsToInvite] = useState([]);
  const [manualTeamName, setManualTeamName] = useState("");
  const [manualTeamEmail, setManualTeamEmail] = useState("");
  const [refereeName, setRefereeName] = useState("");
  const [refereeEmail, setRefereeEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");

  // Modal category selection (for inviting teams to a specific category)
  const [modalCategoryId, setModalCategoryId] = useState(selectedCategory?._id || "");

  // Loading & Action Menu
  const [isLoading, setIsLoading] = useState(false);
  const [actionMenu, setActionMenu] = useState({
    isOpen: false,
    item: null,
    position: { top: 0, left: 0 },
  });

  // Derive the category used in the modal (may differ from the page-level selectedCategory)
  const categories = useMemo(() => selectedTournament?.categories || [], [selectedTournament]);
  const modalCategory = useMemo(
    () => categories.find((c) => c._id === modalCategoryId) || selectedCategory,
    [categories, modalCategoryId, selectedCategory]
  );

  // Members Local State (tournament-scoped)
  const [adminsLocal, setAdminsLocal] = useState([]);
  const [refereesLocal, setRefereesLocal] = useState([]);

  // ============================================
  // DERIVED DATA
  // ============================================

  const teamsData = useMemo(() => {
    const allTeams = [];
    const categoriesToUse = categories.length > 0 ? categories : (selectedCategory ? [selectedCategory] : []);
    for (const cat of categoriesToUse) {
      if (cat?.teams?.length > 0) {
        for (const team of cat.teams) {
          const resolvedId = team.teamId?.id || team.teamId?._id || team._id;
          const resolvedName = team.teamId?.title || team.teamTitle || "Unknown Team";
          if (resolvedId) {
            allTeams.push({
              id: resolvedId,
              name: resolvedName,
              crest: team.teamId?.crest || team.teamCrest || teamBadgePlaceholder,
              email: team.teamId?.email || "No email",
              coach: team.teamId?.email || "No coach email",
              isDeleted: team.isDeleted || false,
              status: team.status,
              squad: team.squad || [],
              category: cat.label || cat.name || "—",
              categoryId: cat._id,
            });
          }
        }
      }
    }
    return allTeams;
  }, [categories, selectedCategory]);

  // Hydrate referees and admins from tournament members
  useEffect(() => {
    if (!selectedTournament?.members) {
      setRefereesLocal([]);
      setAdminsLocal([]);
      return;
    }
    setRefereesLocal(
      selectedTournament.members.filter((m) => m.role === "referee") || []
    );
    setAdminsLocal(
      selectedTournament.members.filter((m) => m.role === "tournamentAdmin") || []
    );
  }, [selectedTournament]);

  const refereesData = useMemo(() => {
    return refereesLocal.map((m) => ({
      id: m._id,
      name: m.userId?.profile?.fullName || m.name || "Unknown",
      image: m.userId?.profile?.image || teamBadgePlaceholder,
      email:
        m.email ||
        m.userId?.email ||
        m.userId?.local?.email ||
        m.userId?.google?.email ||
        "",
      status: m.status,
    }));
  }, [refereesLocal]);

  const adminsData = useMemo(() => {
    return adminsLocal.map((m) => ({
      id: m._id,
      name: m.userId?.profile?.fullName || m.name || "Unknown",
      image: m.userId?.profile?.image || teamBadgePlaceholder,
      email:
        m.email ||
        m.userId?.email ||
        m.userId?.local?.email ||
        m.userId?.google?.email ||
        "",
      role: "Tournament Admin",
      status: m.status,
    }));
  }, [adminsLocal]);

  const currentData = useMemo(() => {
    switch (activeTab) {
      case TAB_TYPES.TEAMS:
        return teamsData;
      case TAB_TYPES.REFEREES:
        return refereesData;
      case TAB_TYPES.ADMINISTRATORS:
        return adminsData;
      default:
        return [];
    }
  }, [activeTab, teamsData, refereesData, adminsData]);

  const filteredData = useMemo(() => {
    let data = currentData;
    // Filter by category
    if (categoryFilter !== "all" && activeTab === TAB_TYPES.TEAMS) {
      data = data.filter((item) => item.category === categoryFilter);
    }
    if (searchQuery.trim()) {
      const lowerQ = searchQuery.toLowerCase();
      data = data.filter(
        (item) =>
          item.name.toLowerCase().includes(lowerQ) ||
          (item.email && item.email.toLowerCase().includes(lowerQ))
      );
    }
    // Sort
    return [...data].sort((a, b) => {
      if (sortBy === "name-asc") {
        return a.name.localeCompare(b.name);
      }
      // "status" — accepted first, then pending, then rejected/other
      const aPriority = STATUS_PRIORITY[a.status] ?? STATUS_PRIORITY.default;
      const bPriority = STATUS_PRIORITY[b.status] ?? STATUS_PRIORITY.default;
      return aPriority - bPriority;
    });
  }, [currentData, searchQuery, sortBy, categoryFilter, activeTab]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeTab, sortBy, categoryFilter]);

  // Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // ============================================
  // HANDLERS
  // ============================================

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedRows([]);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredData.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleOpenAddModal = (type) => {
    setAddModalType(type);
    setModalSearchQuery("");
    setSelectedTeamsToInvite([]);
    setManualTeamName("");
    setManualTeamEmail("");
    setRefereeName("");
    setRefereeEmail("");
    setAdminName("");
    setAdminEmail("");
    setModalCategoryId(selectedCategory?._id || "");
    setIsAddModalOpen(true);
  };

  const handleModalSearch = (query) => {
    setModalSearchQuery(query);
    if (query.trim()) {
      dispatch(
        searchTeams({
          query,
          sportType: selectedTournament?.sport,
        })
      );
    }
  };

  const handleTeamSelectForInvite = (team) => {
    const teamId = team._id || team.id;
    const targetCategory = modalCategory;
    const isAlreadyIn = targetCategory?.teams?.some(
      (t) =>
        (t.teamId && (t.teamId.id === teamId || t.teamId._id === teamId)) ||
        t.teamId === teamId
    );
    if (isAlreadyIn) {
      toast.error("Team is already in this category");
      return;
    }
    // Toggle: remove if already selected, add if not
    setSelectedTeamsToInvite((prev) => {
      const alreadySelected = prev.some((t) => (t._id || t.id) === teamId);
      if (alreadySelected) {
        return prev.filter((t) => (t._id || t.id) !== teamId);
      }
      return [...prev, team];
    });
  };

  const handleRemoveSelectedTeam = (teamId) => {
    setSelectedTeamsToInvite((prev) =>
      prev.filter((t) => (t._id || t.id) !== teamId)
    );
  };

  const handleInviteTeam = async () => {
    if (selectedTeamsToInvite.length === 0) {
      toast.error("Please select at least one team to invite");
      return;
    }

    const targetCategory = modalCategory;
    if (!selectedTournament?.id || !targetCategory?._id) {
      toast.error("Tournament context is missing. Please refresh the page.");
      return;
    }

    const count = selectedTeamsToInvite.length;
    const loadingToast = toast.loading(`Inviting ${count} team${count > 1 ? "s" : ""}...`);

    try {
      setIsLoading(true);

      for (const team of selectedTeamsToInvite) {
        const teamData = {
          teamId: team.id || team._id,
          status: fixedValues.teamStatus.PENDING,
          invitedOn: new Date().toISOString(),
          registrationFeeStatus: fixedValues.registrationFeeStatus.PENDING,
          inviteValidTill: moment().add(7, "days").toISOString(),
        };

        await dispatch(
          inviteTeam({
            tournamentId: selectedTournament.id,
            categoryId: targetCategory._id,
            teamData,
          })
        ).unwrap();

        participantAdded({
          participantType: PARTICIPANT_TYPE.CLUBBIE_TEAM,
          competitionId: selectedTournament.id,
          categoryId: targetCategory._id,
        });
      }

      toast.dismiss(loadingToast);
      toast.success(`${count} team${count > 1 ? "s" : ""} invited successfully!`);
      setIsAddModalOpen(false);
      setSelectedTeamsToInvite([]);
      setModalSearchQuery("");
    } catch (error) {
      toast.dismiss(loadingToast);

      if (error.message?.includes("already invited") || error.message?.includes("duplicate")) {
        toast.error("One or more teams have already been invited to this season");
      } else if (error.message?.includes("not found")) {
        toast.error("Team not found. Please try searching again.");
      } else if (error.message?.includes("network") || error.message?.includes("timeout")) {
        toast.error("Network error. Please check your connection and try again.");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to invite teams");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || "Invalid team data");
      } else {
        toast.error(error.message || "Failed to invite team. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateManualTeam = async () => {
    // Validation
    const teamName = manualTeamName.trim();
    const teamEmail = manualTeamEmail.trim();

    if (!teamName) {
      toast.error("Please enter a team name");
      return;
    }

    if (teamName.length < 2) {
      toast.error("Team name must be at least 2 characters long");
      return;
    }

    if (!teamEmail) {
      toast.error("Please enter a team email");
      return;
    }

    if (!validateEmail(teamEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const targetCategory = modalCategory;
    if (!selectedTournament?.id || !targetCategory?._id) {
      toast.error("Tournament context is missing. Please refresh the page.");
      return;
    }

    // Check for duplicate team names
    const isDuplicateName = targetCategory?.teams?.some(
      (t) => t.teamId?.title?.toLowerCase() === teamName.toLowerCase()
    );

    if (isDuplicateName) {
      toast.error(`A team named "${teamName}" already exists in this category`);
      return;
    }

    const loadingToast = toast.loading(`Creating team "${teamName}"...`);

    try {
      setIsLoading(true);

      // Step 1: Create the team
      const newTeam = await dispatch(
        createCustomTeam({
          title: teamName,
          email: teamEmail,
          sport: selectedTournament?.sport,
        })
      ).unwrap();

      if (!newTeam?.id && !newTeam?._id) {
        throw new Error("Team created but ID is missing");
      }

      // Step 2: Invite the team to the category
      const teamData = {
        teamId: newTeam.id || newTeam._id,
        status: fixedValues.teamStatus.PENDING,
        invitedOn: new Date().toISOString(),
        registrationFeeStatus: fixedValues.registrationFeeStatus.PENDING,
        inviteValidTill: moment().add(7, "days").toISOString(),
      };

      await dispatch(
        inviteTeam({
          tournamentId: selectedTournament.id,
          categoryId: targetCategory._id,
          teamData,
        })
      ).unwrap();

      participantAdded({
        participantType: PARTICIPANT_TYPE.CUSTOM_TEAM,
        competitionId: selectedTournament.id,
        categoryId: targetCategory._id,
      });

      toast.dismiss(loadingToast);
      toast.success(`Team "${teamName}" created and invited successfully!`);
      setIsAddModalOpen(false);
      setManualTeamName("");
      setManualTeamEmail("");
    } catch (error) {
      toast.dismiss(loadingToast);

      // Handle specific error cases
      if (error.message?.includes("already exists") || error.message?.includes("duplicate")) {
        toast.error(`A team with this name or email already exists`);
      } else if (error.message?.includes("email")) {
        toast.error("Invalid email address or email already in use");
      } else if (error.message?.includes("network") || error.message?.includes("timeout")) {
        toast.error("Network error. Please check your connection and try again.");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to create teams");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || "Invalid team data provided");
      } else {
        toast.error(error.message || "Failed to create team. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddReferee = async () => {
    // Validation
    const name = refereeName.trim();
    const email = refereeEmail.trim();

    if (!name) {
      toast.error("Please enter referee name");
      return;
    }

    if (name.length < 2) {
      toast.error("Referee name must be at least 2 characters long");
      return;
    }

    if (!email) {
      toast.error("Please enter referee email");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!selectedTournament?.id) {
      toast.error("Tournament context is missing. Please refresh the page.");
      return;
    }

    // Check for duplicate email
    const isDuplicateEmail = refereesLocal.some(
      (r) => {
        const refEmail = r.email || r.userId?.email || r.userId?.local?.email || r.userId?.google?.email;
        return refEmail?.toLowerCase() === email.toLowerCase();
      }
    );

    if (isDuplicateEmail) {
      toast.error(`A referee with email "${email}" is already added`);
      return;
    }

    const loadingToast = toast.loading(`Inviting referee "${name}"...`);

    try {
      setIsLoading(true);
      const result = await dispatch(
        inviteMember({
          tournamentId: selectedTournament.id,
          payload: {
            email: email,
            name: name,
            role: "referee",
          },
        })
      ).unwrap();

      const newReferee = {
        _id: result?._id || Date.now().toString(),
        userId: result?.userId ? { _id: result.userId, profile: { fullName: name }, email: email } : null,
        name: result?.fullName || name,
        email: result?.email || email,
        role: "referee",
        status: result?.status || "PENDING",
      };

      setRefereesLocal((prev) => [...prev, newReferee]);

      participantAdded({
        participantType: PARTICIPANT_TYPE.COMPETITION_REFEREE,
        competitionId: selectedTournament.id,
      });

      toast.dismiss(loadingToast);
      toast.success(`Referee "${name}" invited successfully!`);
      setIsAddModalOpen(false);
      setRefereeName("");
      setRefereeEmail("");
    } catch (error) {
      toast.dismiss(loadingToast);

      // Handle specific error cases
      if (error.message?.includes("already exists") || error.message?.includes("duplicate")) {
        toast.error("A user with this email already exists in the system");
      } else if (error.message?.includes("email")) {
        toast.error("Invalid email address or email already in use");
      } else if (error.message?.includes("network") || error.message?.includes("timeout")) {
        toast.error("Network error. Please check your connection and try again.");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to add referees");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || "Invalid referee data provided");
      } else if (error.response?.status === 409) {
        toast.error("This referee is already assigned to this season");
      } else {
        toast.error(error.message || "Failed to add referee. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAdministrator = async () => {
    // Validation
    const name = adminName.trim();
    const email = adminEmail.trim();

    if (!name) {
      toast.error("Please enter administrator name");
      return;
    }

    if (name.length < 2) {
      toast.error("Administrator name must be at least 2 characters long");
      return;
    }

    if (!email) {
      toast.error("Please enter administrator email");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!selectedTournament?.id) {
      toast.error("Tournament context is missing. Please refresh the page.");
      return;
    }

    // Check for duplicate email
    const isDuplicateEmail = adminsLocal.some(
      (a) => {
        const aEmail = a.email || a.userId?.email || a.userId?.local?.email || a.userId?.google?.email;
        return aEmail?.toLowerCase() === email.toLowerCase();
      }
    );

    if (isDuplicateEmail) {
      toast.error(`An administrator with email "${email}" is already added`);
      return;
    }

    const loadingToast = toast.loading(`Inviting administrator "${name}"...`);

    try {
      setIsLoading(true);
      const result = await dispatch(
        inviteMember({
          tournamentId: selectedTournament.id,
          payload: {
            email: email,
            name: name,
            role: "tournamentAdmin",
          },
        })
      ).unwrap();

      const newAdmin = {
        _id: result?._id || Date.now().toString(),
        userId: result?.userId ? { _id: result.userId, profile: { fullName: name }, email: email } : null,
        name: result?.fullName || name,
        email: result?.email || email,
        role: "tournamentAdmin",
        status: result?.status || "PENDING",
      };

      setAdminsLocal((prev) => [...prev, newAdmin]);

      participantAdded({
        participantType: PARTICIPANT_TYPE.COMPETITION_ADMIN,
        competitionId: selectedTournament.id,
      });

      toast.dismiss(loadingToast);
      toast.success(`Administrator "${name}" invited successfully!`);
      setIsAddModalOpen(false);
      setAdminName("");
      setAdminEmail("");
    } catch (error) {
      toast.dismiss(loadingToast);

      // Handle specific error cases
      if (error.message?.includes("already exists") || error.message?.includes("duplicate")) {
        toast.error("A user with this email already exists in the system");
      } else if (error.message?.includes("email")) {
        toast.error("Invalid email address or email already in use");
      } else if (error.message?.includes("network") || error.message?.includes("timeout")) {
        toast.error("Network error. Please check your connection and try again.");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to add administrators");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.message || "Invalid administrator data provided");
      } else if (error.response?.status === 409) {
        toast.error("This administrator is already assigned to this tournament");
      } else {
        toast.error(error.message || "Failed to add administrator. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) {
      toast.error("No item selected for deletion");
      return;
    }

    const teamCategoryId = itemToDelete.categoryId || selectedCategory?._id;

    if (!selectedTournament?.id || !teamCategoryId) {
      toast.error("Tournament context is missing. Please refresh the page.");
      return;
    }

    const itemName = itemToDelete.name || "item";
    const loadingToast = toast.loading(`Removing ${itemName}...`);

    try {
      setIsLoading(true);

      if (activeTab === TAB_TYPES.TEAMS) {
        await dispatch(
          deleteTeam({
            tournamentId: selectedTournament.id,
            categoryId: teamCategoryId,
            teamId: itemToDelete.id,
          })
        ).unwrap();

        const sourceCategory = categories.find((c) => c._id === teamCategoryId) || selectedCategory;
        const updatedCategory = {
          ...sourceCategory,
          teams: (sourceCategory?.teams || []).filter(
            (t) => {
              const teamMatchesId = t.teamId && (t.teamId.id === itemToDelete.id || t.teamId._id === itemToDelete.id);
              const subdocMatchesId = t._id === itemToDelete.id || t._id?.toString() === itemToDelete.id;
              return !(teamMatchesId || subdocMatchesId);
            }
          ),
        };

        dispatch(
          updateCategoryLocally({
            tournamentId: selectedTournament.id,
            categoryId: teamCategoryId,
            updatedCategory,
          })
        );

        toast.dismiss(loadingToast);
        toast.success(`Team "${itemName}" removed successfully`);
      } else if (activeTab === TAB_TYPES.REFEREES) {
        await dispatch(
          deleteMember({
            tournamentId: selectedTournament.id,
            memberId: itemToDelete.id,
          })
        ).unwrap();

        setRefereesLocal((prev) => prev.filter((m) => m._id !== itemToDelete.id));
        toast.dismiss(loadingToast);
        toast.success(`Referee "${itemName}" removed successfully`);
      } else if (activeTab === TAB_TYPES.ADMINISTRATORS) {
        await dispatch(
          deleteMember({
            tournamentId: selectedTournament.id,
            memberId: itemToDelete.id,
          })
        ).unwrap();

        setAdminsLocal((prev) => prev.filter((m) => m._id !== itemToDelete.id));
        toast.dismiss(loadingToast);
        toast.success(`Administrator "${itemName}" removed successfully`);
      }

      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      setSelectedRows(selectedRows.filter((id) => id !== itemToDelete.id));
    } catch (error) {
      toast.dismiss(loadingToast);

      // Handle specific error cases
      if (error.message?.includes("not found")) {
        toast.error(`${itemName} not found. It may have already been removed.`);
      } else if (error.message?.includes("in use") || error.message?.includes("referenced")) {
        toast.error(`Cannot remove ${itemName}. It is currently in use.`);
      } else if (error.message?.includes("network") || error.message?.includes("timeout")) {
        toast.error("Network error. Please check your connection and try again.");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to remove this item");
      } else if (error.response?.status === 404) {
        toast.error(`${itemName} not found. It may have already been removed.`);
      } else if (error.response?.status === 409) {
        toast.error(`Cannot remove ${itemName}. Please resolve any dependencies first.`);
      } else {
        toast.error(error.message || `Failed to remove ${itemName}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error("No items selected for deletion");
      return;
    }

    if (!selectedTournament?.id || !selectedCategory?._id) {
      toast.error("Tournament context is missing. Please refresh the page.");
      return;
    }

    const count = selectedRows.length;
    const itemType = activeTab === TAB_TYPES.TEAMS ? "team" :
      activeTab === TAB_TYPES.REFEREES ? "referee" : "administrator";
    const itemTypePlural = count > 1 ? `${itemType}s` : itemType;

    const loadingToast = toast.loading(`Removing ${count} ${itemTypePlural}...`);

    try {
      setIsLoading(true);

      if (activeTab === TAB_TYPES.TEAMS) {
        await dispatch(
          bulkDeleteTeams({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategory._id,
            teamIds: selectedRows,
          })
        ).unwrap();

        const updatedCategory = {
          ...selectedCategory,
          teams: selectedCategory.teams.filter(
            (t) => !t.teamId || (!selectedRows.includes(t.teamId.id) && !selectedRows.includes(t.teamId._id))
          ),
        };

        dispatch(
          updateCategoryLocally({
            tournamentId: selectedTournament.id,
            categoryId: selectedCategory._id,
            updatedCategory,
          })
        );

        toast.dismiss(loadingToast);
        toast.success(`${count} ${itemTypePlural} removed successfully`);
      } else if (activeTab === TAB_TYPES.REFEREES) {
        await dispatch(
          bulkDeleteMembers({
            tournamentId: selectedTournament.id,
            memberIds: selectedRows,
          })
        ).unwrap();

        setRefereesLocal((prev) =>
          prev.filter((m) => !selectedRows.includes(m._id))
        );
        toast.dismiss(loadingToast);
        toast.success(`${count} ${itemTypePlural} removed successfully`);
      } else if (activeTab === TAB_TYPES.ADMINISTRATORS) {
        await dispatch(
          bulkDeleteMembers({
            tournamentId: selectedTournament.id,
            memberIds: selectedRows,
          })
        ).unwrap();

        setAdminsLocal((prev) =>
          prev.filter((m) => !selectedRows.includes(m._id))
        );
        toast.dismiss(loadingToast);
        toast.success(`${count} ${itemTypePlural} removed successfully`);
      }

      setSelectedRows([]);
    } catch (error) {
      toast.dismiss(loadingToast);

      // Handle specific error cases
      if (error.message?.includes("partial")) {
        toast.error(`Some ${itemTypePlural} could not be removed. Please refresh and try again.`);
      } else if (error.message?.includes("in use") || error.message?.includes("referenced")) {
        toast.error(`Cannot remove selected ${itemTypePlural}. Some are currently in use.`);
      } else if (error.message?.includes("network") || error.message?.includes("timeout")) {
        toast.error("Network error. Please check your connection and try again.");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to remove these items");
      } else if (error.response?.status === 404) {
        toast.error("Some items were not found. They may have already been removed.");
      } else if (error.response?.status === 409) {
        toast.error(`Cannot remove selected ${itemTypePlural}. Please resolve any dependencies first.`);
      } else {
        toast.error(error.message || `Failed to remove ${itemTypePlural}. Please try again.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewTeam = (teamId) => {
    navigate(DASHBOARD_STACK.TEAM_OVERVIEW.replace(":teamId", teamId));
  };

  const handleResendInvite = async (item) => {
    if (!selectedTournament?.id || !item.categoryId || !item.id) return;
    const loadingToast = toast.loading("Resending invite...");
    try {
      await authApi.post(
        `tournaments/${selectedTournament.id}/categories/${item.categoryId}/teams/${item.id}/resend-invite`
      );
      toast.dismiss(loadingToast);
      toast.success("Invitation resent successfully");
    } catch (error) {
      toast.dismiss(loadingToast);
      const msg = error.response?.data?.message || error.message || "Failed to resend invite";
      toast.error(msg);
    }
  };

  const handleOpenActionMenu = (e, item) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 200;

    setActionMenu({
      isOpen: !actionMenu.isOpen || actionMenu.item?.id !== item.id,
      item: item,
      position: {
        top: rect.top,
        left: rect.right - menuWidth*1.05,
      },
    });
  };

  // Close action menu on scroll or click outside
  useEffect(() => {
    const handleActionMenuClose = (event) => {
      if (
        actionMenu.isOpen &&
        !event.target.closest(".action-menu-popup") &&
        !event.target.closest(".action-menu-trigger")
      ) {
        setActionMenu((prev) => ({ ...prev, isOpen: false }));
      }
    };

    const handleScroll = () => {
      setActionMenu((prev) => ({ ...prev, isOpen: false }));
    };

    window.addEventListener("mousedown", handleActionMenuClose);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("mousedown", handleActionMenuClose);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [actionMenu.isOpen]);

  // ============================================
  // RENDER
  // ============================================

  const isEmpty = filteredData.length === 0;

  return (
    <div className={`bg-[#000F1F] text-[#FEFCEB] p-3 sm:p-[18px] lg:px-8 font-sans ${isEmpty ? "h-full flex flex-col" : "min-h-screen"}`}>
      {/* Header */}
      <div className="flex flex-col gap-5 mb-4">
        <Header activeTab={activeTab} onOpenAddModal={handleOpenAddModal} canManageParticipants={permissions.canManageParticipants} />
        <TabNav activeTab={activeTab} onTabChange={handleTabChange} />
        {/* Only show SearchFilters when there's data to search */}
        {currentData.length > 0 && (
          <SearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCount={selectedRows.length}
            activeTab={activeTab}
            onBulkDelete={handleBulkDelete}
            sortBy={sortBy}
            onSortChange={setSortBy}
            categories={categories}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={setCategoryFilter}
          />
        )}
      </div>

      {/* Content */}
      {filteredData.length === 0 ? (
        <EmptyState />
      ) : (
        <ParticipantsTable
          data={paginatedData}
          activeTab={activeTab}
          selectedRows={selectedRows}
          showCategoryColumn={activeTab === TAB_TYPES.TEAMS && categories.length > 1}
          onSelectAll={handleSelectAll}
          onSelectRow={handleSelectRow}
          onViewTeam={handleViewTeam}
          onOpenActionMenu={handleOpenActionMenu}
          onOpenDeleteModal={handleOpenDeleteModal}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          startIndex={startIndex}
          endIndex={endIndex}
          rowsPerPage={rowsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={(val) => {
            setRowsPerPage(val);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Modals */}
      <AddModal
        isOpen={isAddModalOpen}
        modalType={addModalType}
        onClose={() => setIsAddModalOpen(false)}
        modalSearchQuery={modalSearchQuery}
        onModalSearch={handleModalSearch}
        searchResults={searchResults}
        isSearching={isSearching}
        selectedTeams={selectedTeamsToInvite}
        onTeamSelect={handleTeamSelectForInvite}
        onTeamRemove={handleRemoveSelectedTeam}
        onInviteTeam={handleInviteTeam}
        manualTeamName={manualTeamName}
        setManualTeamName={setManualTeamName}
        manualTeamEmail={manualTeamEmail}
        setManualTeamEmail={setManualTeamEmail}
        onCreateManualTeam={handleCreateManualTeam}
        refereeName={refereeName}
        setRefereeName={setRefereeName}
        refereeEmail={refereeEmail}
        setRefereeEmail={setRefereeEmail}
        onAddReferee={handleAddReferee}
        adminName={adminName}
        setAdminName={setAdminName}
        adminEmail={adminEmail}
        setAdminEmail={setAdminEmail}
        onAddAdministrator={handleAddAdministrator}
        isLoading={isLoading}
        categories={categories}
        selectedCategoryId={modalCategoryId}
        onCategoryChange={setModalCategoryId}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        activeTab={activeTab}
        itemToDelete={itemToDelete}
        isLoading={isLoading}
        onConfirm={handleConfirmDelete}
        onClose={() => setIsDeleteModalOpen(false)}
      />

      {/* Action Menu */}
      <ActionMenu
        isOpen={actionMenu.isOpen}
        item={actionMenu.item}
        position={actionMenu.position}
        activeTab={activeTab}
        onViewTeam={handleViewTeam}
        onDelete={handleOpenDeleteModal}
        onResendInvite={handleResendInvite}
        onClose={() => setActionMenu((prev) => ({ ...prev, isOpen: false }))}
        canManageParticipants={permissions.canManageParticipants}
      />
    </div>
  );
};

export default Participants;
